import { uploadCroppedPicture } from './uploadCroppedPicture';
import {
  PROFILE_PICTURE_CONFLICT,
  IDEMPOTENCY_KEY_REUSED,
  isCommittedResult,
  isPendingResult,
  resolveRetryDelayMs,
  readErrorPayload,
  errorCodeFromPayload,
} from './lifecycleHelpers';
import { clearResumeState, loadResumeState, saveResumeState } from './resumeState';

export {
  PROFILE_PICTURE_CONFLICT,
  IDEMPOTENCY_KEY_REUSED,
  isCommittedResult,
  isPendingResult,
};

const MAX_POLL_ATTEMPTS = 40;

/** Resume-storage target for the signed-in user's own picture (never Cognito sub / numeric IDM id). */
export const SELF_PROFILE_PICTURE_TARGET = 'me';

/**
 * @typedef {object} ManagedProfilePictureApi
 * @property {(slug: string, userId: number) => Promise<object>} getOrganisationUserProfilePicture
 * @property {(slug: string, userId: number, expected: string|null, key: string) => Promise<object>} prepareOrganisationUserProfilePicture
 * @property {(slug: string, userId: number, expected: string|null, key: string) => Promise<object>} clearOrganisationUserProfilePicture
 * @property {(operationId: string) => Promise<object>} getProfilePictureOperation
 * @property {(operationId: string) => Promise<object>} finaliseProfilePictureOperation
 */

/**
 * @typedef {object} OwnProfilePictureApi
 * @property {() => Promise<object>} getOwnProfilePicture
 * @property {(expected: string|null, key: string) => Promise<object>} prepareOwnProfilePicture
 * @property {(expected: string|null, key: string) => Promise<object>} clearOwnProfilePicture
 * @property {(operationId: string) => Promise<object>} getProfilePictureOperation
 * @property {(operationId: string) => Promise<object>} finaliseProfilePictureOperation
 */

async function sleep(ms, sleepFn) {
  return sleepFn(ms);
}

async function pollUntilCommitted({
  api,
  operationId,
  action,
  retryClear,
  onPending,
  sleepFn,
  maxAttempts = MAX_POLL_ATTEMPTS,
}) {
  let attempt = 0;
  let lastResult = null;

  while (attempt < maxAttempts) {
    // Observation GET does not commit. Retry uncommitted replacement via finalise POST;
    // retry clear via the original DELETE with the same UUID and expected value.
    if (action === 'replace') {
      lastResult = await api.finaliseProfilePictureOperation(operationId);
    } else {
      lastResult = await retryClear();
    }

    if (isCommittedResult(lastResult)) {
      return lastResult;
    }

    if (!isPendingResult(lastResult)) {
      return lastResult;
    }

    if (onPending) {
      onPending(lastResult);
    }

    await sleep(resolveRetryDelayMs(lastResult, attempt), sleepFn);
    attempt += 1;

    // Between mutation retries, observe status without treating GET as a commit path.
    const observed = await api.getProfilePictureOperation(operationId);
    if (isCommittedResult(observed)) {
      return observed;
    }
    lastResult = observed;
    if (!isPendingResult(observed)) {
      return observed;
    }
  }

  const timeoutError = new Error('profile-picture-poll-timeout');
  timeoutError.lastResult = lastResult;
  throw timeoutError;
}

async function classifyThrownError(error, getCanonical) {
  const payload = await readErrorPayload(error);
  const code = errorCodeFromPayload(payload);

  if (
    code !== IDEMPOTENCY_KEY_REUSED &&
    (code === PROFILE_PICTURE_CONFLICT || payload.status === 409)
  ) {
    let canonical = null;
    try {
      canonical = await getCanonical();
    } catch (refreshError) {
      // Keep going with body pair if present.
    }
    if (!canonical && payload.body) {
      canonical = {
        targetUserId: payload.body.target_user_id,
        profilePictureImageId:
          payload.body.profile_picture_image_id !== undefined
            ? payload.body.profile_picture_image_id
            : null,
        profilePictureUrl:
          payload.body.profile_picture_url !== undefined ? payload.body.profile_picture_url : null,
      };
    }
    return {
      type: 'conflict',
      code: PROFILE_PICTURE_CONFLICT,
      canonical,
      error,
    };
  }

  if (code === IDEMPOTENCY_KEY_REUSED) {
    return { type: 'idempotency', code: IDEMPOTENCY_KEY_REUSED, error };
  }

  if (payload.status === 410) {
    return { type: 'expired', code: code || 'UPLOAD_EXPIRED', error };
  }

  if (payload.status === 422) {
    return { type: 'invalid', code: code || 'INVALID_UPLOAD', error };
  }

  if (payload.status === 503) {
    return {
      type: 'attention',
      code: code || 'ATTENTION_REQUIRED',
      committed: false,
      error,
      body: payload.body,
    };
  }

  return { type: 'error', code, error, status: payload.status, body: payload.body };
}

function managedGetCanonical(api, organisationSlug, userId) {
  return () => api.getOrganisationUserProfilePicture(organisationSlug, userId);
}

function managedRetryClear(api, organisationSlug, userId, expectedImageId, idempotencyKey) {
  return () =>
    api.clearOrganisationUserProfilePicture(
      organisationSlug,
      userId,
      expectedImageId,
      idempotencyKey,
    );
}

/**
 * Refresh canonical picture and compare to the editor's displayed expected id.
 * If they differ, require deliberate reapplication (do not silently adopt).
 */
export async function refreshCanonicalBeforeAction({
  api,
  organisationSlug,
  userId,
  displayedExpectedImageId,
}) {
  const canonical = await api.getOrganisationUserProfilePicture(organisationSlug, userId);
  const remoteId =
    canonical.profilePictureImageId === undefined ? null : canonical.profilePictureImageId;
  const displayed = displayedExpectedImageId === undefined ? null : displayedExpectedImageId;
  if (remoteId !== displayed) {
    return { ok: false, reason: 'stale', canonical };
  }
  return { ok: true, canonical };
}

export async function refreshOwnCanonicalBeforeAction({ api, displayedExpectedImageId }) {
  const canonical = await api.getOwnProfilePicture();
  const remoteId =
    canonical.profilePictureImageId === undefined ? null : canonical.profilePictureImageId;
  const displayed = displayedExpectedImageId === undefined ? null : displayedExpectedImageId;
  if (remoteId !== displayed) {
    return { ok: false, reason: 'stale', canonical };
  }
  return { ok: true, canonical };
}

/**
 * Save a cropped candidate for a managed Organisation User.
 */
export async function saveManagedProfilePicture({
  api,
  organisationSlug,
  userId,
  displayedExpectedImageId,
  croppedBlob,
  idempotencyKey,
  actorId,
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  upload = uploadCroppedPicture,
  sleepFn = ms => new Promise(resolve => setTimeout(resolve, ms)),
  onPending,
}) {
  const refresh = await refreshCanonicalBeforeAction({
    api,
    organisationSlug,
    userId,
    displayedExpectedImageId,
  });
  if (!refresh.ok) {
    return { status: 'stale', canonical: refresh.canonical };
  }

  const expectedImageId = displayedExpectedImageId === undefined ? null : displayedExpectedImageId;

  // Resume a retained operation before another upload when one exists.
  if (storage && actorId != null) {
    const resume = loadResumeState(storage, actorId, userId);
    if (resume && resume.action === 'replace' && resume.operationId) {
      try {
        const observed = await api.getProfilePictureOperation(resume.operationId);
        if (isCommittedResult(observed)) {
          clearResumeState(storage, actorId, userId);
          return { status: 'committed', result: observed, canonical: mapCanonicalFromOperation(observed) };
        }
        if (observed.operation && observed.operation.status === 'awaiting_upload' && croppedBlob) {
          if (observed.operation.uploadUrl) {
            await upload(observed.operation.uploadUrl, croppedBlob);
          }
        }
        const finalised = await pollUntilCommitted({
          api,
          operationId: resume.operationId,
          action: 'replace',
          retryClear: managedRetryClear(api, organisationSlug, userId, resume.expectedImageId, resume.idempotencyKey),
          onPending,
          sleepFn,
        });
        if (isCommittedResult(finalised)) {
          clearResumeState(storage, actorId, userId);
          return {
            status: 'committed',
            result: finalised,
            canonical: mapCanonicalFromOperation(finalised),
          };
        }
      } catch (error) {
        const classified = await classifyThrownError(error, managedGetCanonical(api, organisationSlug, userId));
        if (classified.type === 'conflict') {
          if (storage && actorId != null) clearResumeState(storage, actorId, userId);
          return { status: 'conflict', canonical: classified.canonical, error };
        }
        // Fall through to a new prepare only when resume cannot continue.
      }
    }
  }

  let prepareResult;
  try {
    prepareResult = await api.prepareOrganisationUserProfilePicture(
      organisationSlug,
      userId,
      expectedImageId,
      idempotencyKey,
    );
  } catch (error) {
    const classified = await classifyThrownError(error, managedGetCanonical(api, organisationSlug, userId));
    if (classified.type === 'conflict') {
      return { status: 'conflict', canonical: classified.canonical, error };
    }
    if (classified.type === 'idempotency') {
      return { status: 'idempotency', error };
    }
    return { status: 'error', error, classified };
  }

  if (storage && actorId != null && prepareResult.operation && prepareResult.operation.operationId) {
    saveResumeState(storage, actorId, userId, {
      operationId: prepareResult.operation.operationId,
      idempotencyKey,
      expectedImageId,
      action: 'replace',
    });
  }

  if (isPendingResult(prepareResult) && prepareResult.operation.status === 'preparing') {
    if (onPending) onPending(prepareResult);
    // Wait/poll prepare until upload URL is available or committed unexpectedly.
    let attempt = 0;
    let current = prepareResult;
    while (isPendingResult(current) && current.operation.status === 'preparing' && attempt < MAX_POLL_ATTEMPTS) {
      await sleep(resolveRetryDelayMs(current, attempt), sleepFn);
      // Retry prepare with the same key — do not create a new UUID.
      try {
        current = await api.prepareOrganisationUserProfilePicture(
          organisationSlug,
          userId,
          expectedImageId,
          idempotencyKey,
        );
      } catch (error) {
        const classified = await classifyThrownError(error, managedGetCanonical(api, organisationSlug, userId));
        if (classified.type === 'conflict') {
          if (storage && actorId != null) clearResumeState(storage, actorId, userId);
          return { status: 'conflict', canonical: classified.canonical, error };
        }
        return { status: 'error', error, classified };
      }
      if (onPending) onPending(current);
      attempt += 1;
    }
    prepareResult = current;
  }

  if (isCommittedResult(prepareResult)) {
    if (storage && actorId != null) clearResumeState(storage, actorId, userId);
    return {
      status: 'committed',
      result: prepareResult,
      canonical: mapCanonicalFromOperation(prepareResult),
    };
  }

  const uploadUrl = prepareResult.operation && prepareResult.operation.uploadUrl;
  const operationId = prepareResult.operation && prepareResult.operation.operationId;

  if (!uploadUrl || !operationId) {
    return { status: 'error', error: new Error('missing-upload-url'), result: prepareResult };
  }

  try {
    await upload(uploadUrl, croppedBlob);
  } catch (error) {
    return { status: 'upload-error', error, result: prepareResult };
  }

  try {
    let finaliseResult = await api.finaliseProfilePictureOperation(operationId);

    if (isCommittedResult(finaliseResult)) {
      if (storage && actorId != null) clearResumeState(storage, actorId, userId);
      return {
        status: 'committed',
        result: finaliseResult,
        canonical: mapCanonicalFromOperation(finaliseResult),
      };
    }

    if (isPendingResult(finaliseResult)) {
      if (onPending) onPending(finaliseResult);
      finaliseResult = await pollUntilCommitted({
        api,
        operationId,
        action: 'replace',
        retryClear: managedRetryClear(api, organisationSlug, userId, expectedImageId, idempotencyKey),
        onPending,
        sleepFn,
      });
    }

    if (isCommittedResult(finaliseResult)) {
      if (storage && actorId != null) clearResumeState(storage, actorId, userId);
      return {
        status: 'committed',
        result: finaliseResult,
        canonical: mapCanonicalFromOperation(finaliseResult),
      };
    }

    // Propagation attention / non-committed terminal
    if (finaliseResult.operation && finaliseResult.operation.status === 'attention_required') {
      return { status: 'attention', result: finaliseResult };
    }

    return { status: 'pending-timeout', result: finaliseResult };
  } catch (error) {
    const classified = await classifyThrownError(error, managedGetCanonical(api, organisationSlug, userId));
    if (classified.type === 'conflict') {
      if (storage && actorId != null) clearResumeState(storage, actorId, userId);
      return { status: 'conflict', canonical: classified.canonical, error };
    }
    if (classified.type === 'expired') {
      return { status: 'expired', error, classified };
    }
    if (classified.type === 'invalid') {
      return { status: 'invalid', error, classified };
    }
    if (classified.type === 'attention') {
      return { status: 'attention', error, classified };
    }
    return { status: 'error', error, classified };
  }
}

/**
 * Clear a managed Organisation User's Profile Picture.
 */
export async function clearManagedProfilePicture({
  api,
  organisationSlug,
  userId,
  displayedExpectedImageId,
  idempotencyKey,
  actorId,
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  sleepFn = ms => new Promise(resolve => setTimeout(resolve, ms)),
  onPending,
}) {
  const refresh = await refreshCanonicalBeforeAction({
    api,
    organisationSlug,
    userId,
    displayedExpectedImageId,
  });
  if (!refresh.ok) {
    return { status: 'stale', canonical: refresh.canonical };
  }

  const expectedImageId = displayedExpectedImageId === undefined ? null : displayedExpectedImageId;

  // Resume a retained clear before presenting a new clear as the same action.
  if (storage && actorId != null) {
    const resume = loadResumeState(storage, actorId, userId);
    if (resume && resume.action === 'clear' && resume.idempotencyKey === idempotencyKey) {
      try {
        let result = await api.clearOrganisationUserProfilePicture(
          organisationSlug,
          userId,
          resume.expectedImageId,
          resume.idempotencyKey,
        );
        if (isPendingResult(result)) {
          if (onPending) onPending(result);
          result = await pollUntilCommitted({
            api,
            operationId: resume.operationId || (result.operation && result.operation.operationId),
            action: 'clear',
            retryClear: managedRetryClear(api, organisationSlug, userId, resume.expectedImageId, resume.idempotencyKey),
            onPending,
            sleepFn,
          });
        }
        if (isCommittedResult(result)) {
          clearResumeState(storage, actorId, userId);
          return { status: 'committed', result, canonical: mapCanonicalFromOperation(result) };
        }
      } catch (error) {
        const classified = await classifyThrownError(error, managedGetCanonical(api, organisationSlug, userId));
        if (classified.type === 'conflict') {
          clearResumeState(storage, actorId, userId);
          return { status: 'conflict', canonical: classified.canonical, error };
        }
      }
    }
  }

  try {
    let result = await api.clearOrganisationUserProfilePicture(
      organisationSlug,
      userId,
      expectedImageId,
      idempotencyKey,
    );

    const operationId = result.operation && result.operation.operationId;
    if (storage && actorId != null && operationId) {
      saveResumeState(storage, actorId, userId, {
        operationId,
        idempotencyKey,
        expectedImageId,
        action: 'clear',
      });
    }

    if (isCommittedResult(result)) {
      if (storage && actorId != null) clearResumeState(storage, actorId, userId);
      return { status: 'committed', result, canonical: mapCanonicalFromOperation(result) };
    }

    if (isPendingResult(result)) {
      if (onPending) onPending(result);
      result = await pollUntilCommitted({
        api,
        operationId,
        action: 'clear',
        retryClear: managedRetryClear(api, organisationSlug, userId, expectedImageId, idempotencyKey),
        onPending,
        sleepFn,
      });
    }

    if (isCommittedResult(result)) {
      if (storage && actorId != null) clearResumeState(storage, actorId, userId);
      return { status: 'committed', result, canonical: mapCanonicalFromOperation(result) };
    }

    if (result.operation && result.operation.status === 'attention_required') {
      return { status: 'attention', result };
    }

    return { status: 'pending-timeout', result };
  } catch (error) {
    const classified = await classifyThrownError(error, managedGetCanonical(api, organisationSlug, userId));
    if (classified.type === 'conflict') {
      if (storage && actorId != null) clearResumeState(storage, actorId, userId);
      return { status: 'conflict', canonical: classified.canonical, error };
    }
    if (classified.type === 'idempotency') {
      return { status: 'idempotency', error };
    }
    if (classified.type === 'attention') {
      return { status: 'attention', error, classified };
    }
    return { status: 'error', error, classified };
  }
}

function ownGetCanonical(api) {
  return () => api.getOwnProfilePicture();
}

function ownRetryClear(api, expectedImageId, idempotencyKey) {
  return () => api.clearOwnProfilePicture(expectedImageId, idempotencyKey);
}

/**
 * Save a cropped candidate for the signed-in Organisation User (self lifecycle).
 * Never sends a numeric IDM user id or Cognito sub as an IDM primary key.
 */
export async function saveOwnProfilePicture({
  api,
  displayedExpectedImageId,
  croppedBlob,
  idempotencyKey,
  actorId,
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  upload = uploadCroppedPicture,
  sleepFn = ms => new Promise(resolve => setTimeout(resolve, ms)),
  onPending,
}) {
  const targetUserId = SELF_PROFILE_PICTURE_TARGET;
  const refresh = await refreshOwnCanonicalBeforeAction({
    api,
    displayedExpectedImageId,
  });
  if (!refresh.ok) {
    return { status: 'stale', canonical: refresh.canonical };
  }

  const expectedImageId = displayedExpectedImageId === undefined ? null : displayedExpectedImageId;

  if (storage && actorId != null) {
    const resume = loadResumeState(storage, actorId, targetUserId);
    if (resume && resume.action === 'replace' && resume.operationId) {
      try {
        const observed = await api.getProfilePictureOperation(resume.operationId);
        if (isCommittedResult(observed)) {
          clearResumeState(storage, actorId, targetUserId);
          return { status: 'committed', result: observed, canonical: mapCanonicalFromOperation(observed) };
        }
        if (observed.operation && observed.operation.status === 'awaiting_upload' && croppedBlob) {
          if (observed.operation.uploadUrl) {
            await upload(observed.operation.uploadUrl, croppedBlob);
          }
        }
        const finalised = await pollUntilCommitted({
          api,
          operationId: resume.operationId,
          action: 'replace',
          retryClear: ownRetryClear(api, resume.expectedImageId, resume.idempotencyKey),
          onPending,
          sleepFn,
        });
        if (isCommittedResult(finalised)) {
          clearResumeState(storage, actorId, targetUserId);
          return {
            status: 'committed',
            result: finalised,
            canonical: mapCanonicalFromOperation(finalised),
          };
        }
      } catch (error) {
        const classified = await classifyThrownError(error, ownGetCanonical(api));
        if (classified.type === 'conflict') {
          if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
          return { status: 'conflict', canonical: classified.canonical, error };
        }
      }
    }
  }

  let prepareResult;
  try {
    prepareResult = await api.prepareOwnProfilePicture(expectedImageId, idempotencyKey);
  } catch (error) {
    const classified = await classifyThrownError(error, ownGetCanonical(api));
    if (classified.type === 'conflict') {
      return { status: 'conflict', canonical: classified.canonical, error };
    }
    if (classified.type === 'idempotency') {
      return { status: 'idempotency', error };
    }
    return { status: 'error', error, classified };
  }

  if (storage && actorId != null && prepareResult.operation && prepareResult.operation.operationId) {
    saveResumeState(storage, actorId, targetUserId, {
      operationId: prepareResult.operation.operationId,
      idempotencyKey,
      expectedImageId,
      action: 'replace',
    });
  }

  if (isPendingResult(prepareResult) && prepareResult.operation.status === 'preparing') {
    if (onPending) onPending(prepareResult);
    let attempt = 0;
    let current = prepareResult;
    while (isPendingResult(current) && current.operation.status === 'preparing' && attempt < MAX_POLL_ATTEMPTS) {
      await sleep(resolveRetryDelayMs(current, attempt), sleepFn);
      try {
        current = await api.prepareOwnProfilePicture(expectedImageId, idempotencyKey);
      } catch (error) {
        const classified = await classifyThrownError(error, ownGetCanonical(api));
        if (classified.type === 'conflict') {
          if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
          return { status: 'conflict', canonical: classified.canonical, error };
        }
        return { status: 'error', error, classified };
      }
      if (onPending) onPending(current);
      attempt += 1;
    }
    prepareResult = current;
  }

  if (isCommittedResult(prepareResult)) {
    if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
    return {
      status: 'committed',
      result: prepareResult,
      canonical: mapCanonicalFromOperation(prepareResult),
    };
  }

  const uploadUrl = prepareResult.operation && prepareResult.operation.uploadUrl;
  const operationId = prepareResult.operation && prepareResult.operation.operationId;

  if (!uploadUrl || !operationId) {
    return { status: 'error', error: new Error('missing-upload-url'), result: prepareResult };
  }

  try {
    await upload(uploadUrl, croppedBlob);
  } catch (error) {
    return { status: 'upload-error', error, result: prepareResult };
  }

  try {
    let finaliseResult = await api.finaliseProfilePictureOperation(operationId);

    if (isCommittedResult(finaliseResult)) {
      if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
      return {
        status: 'committed',
        result: finaliseResult,
        canonical: mapCanonicalFromOperation(finaliseResult),
      };
    }

    if (isPendingResult(finaliseResult)) {
      if (onPending) onPending(finaliseResult);
      finaliseResult = await pollUntilCommitted({
        api,
        operationId,
        action: 'replace',
        retryClear: ownRetryClear(api, expectedImageId, idempotencyKey),
        onPending,
        sleepFn,
      });
    }

    if (isCommittedResult(finaliseResult)) {
      if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
      return {
        status: 'committed',
        result: finaliseResult,
        canonical: mapCanonicalFromOperation(finaliseResult),
      };
    }

    if (finaliseResult.operation && finaliseResult.operation.status === 'attention_required') {
      return { status: 'attention', result: finaliseResult };
    }

    return { status: 'pending-timeout', result: finaliseResult };
  } catch (error) {
    const classified = await classifyThrownError(error, ownGetCanonical(api));
    if (classified.type === 'conflict') {
      if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
      return { status: 'conflict', canonical: classified.canonical, error };
    }
    if (classified.type === 'expired') {
      return { status: 'expired', error, classified };
    }
    if (classified.type === 'invalid') {
      return { status: 'invalid', error, classified };
    }
    if (classified.type === 'attention') {
      return { status: 'attention', error, classified };
    }
    return { status: 'error', error, classified };
  }
}

/**
 * Clear the signed-in Organisation User's Profile Picture (self lifecycle).
 */
export async function clearOwnProfilePicture({
  api,
  displayedExpectedImageId,
  idempotencyKey,
  actorId,
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  sleepFn = ms => new Promise(resolve => setTimeout(resolve, ms)),
  onPending,
}) {
  const targetUserId = SELF_PROFILE_PICTURE_TARGET;
  const refresh = await refreshOwnCanonicalBeforeAction({
    api,
    displayedExpectedImageId,
  });
  if (!refresh.ok) {
    return { status: 'stale', canonical: refresh.canonical };
  }

  const expectedImageId = displayedExpectedImageId === undefined ? null : displayedExpectedImageId;

  if (storage && actorId != null) {
    const resume = loadResumeState(storage, actorId, targetUserId);
    if (resume && resume.action === 'clear' && resume.idempotencyKey === idempotencyKey) {
      try {
        let result = await api.clearOwnProfilePicture(
          resume.expectedImageId,
          resume.idempotencyKey,
        );
        if (isPendingResult(result)) {
          if (onPending) onPending(result);
          result = await pollUntilCommitted({
            api,
            operationId: resume.operationId || (result.operation && result.operation.operationId),
            action: 'clear',
            retryClear: ownRetryClear(api, resume.expectedImageId, resume.idempotencyKey),
            onPending,
            sleepFn,
          });
        }
        if (isCommittedResult(result)) {
          clearResumeState(storage, actorId, targetUserId);
          return { status: 'committed', result, canonical: mapCanonicalFromOperation(result) };
        }
      } catch (error) {
        const classified = await classifyThrownError(error, ownGetCanonical(api));
        if (classified.type === 'conflict') {
          clearResumeState(storage, actorId, targetUserId);
          return { status: 'conflict', canonical: classified.canonical, error };
        }
      }
    }
  }

  try {
    let result = await api.clearOwnProfilePicture(expectedImageId, idempotencyKey);

    const operationId = result.operation && result.operation.operationId;
    if (storage && actorId != null && operationId) {
      saveResumeState(storage, actorId, targetUserId, {
        operationId,
        idempotencyKey,
        expectedImageId,
        action: 'clear',
      });
    }

    if (isCommittedResult(result)) {
      if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
      return { status: 'committed', result, canonical: mapCanonicalFromOperation(result) };
    }

    if (isPendingResult(result)) {
      if (onPending) onPending(result);
      result = await pollUntilCommitted({
        api,
        operationId,
        action: 'clear',
        retryClear: ownRetryClear(api, expectedImageId, idempotencyKey),
        onPending,
        sleepFn,
      });
    }

    if (isCommittedResult(result)) {
      if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
      return { status: 'committed', result, canonical: mapCanonicalFromOperation(result) };
    }

    if (result.operation && result.operation.status === 'attention_required') {
      return { status: 'attention', result };
    }

    return { status: 'pending-timeout', result };
  } catch (error) {
    const classified = await classifyThrownError(error, ownGetCanonical(api));
    if (classified.type === 'conflict') {
      if (storage && actorId != null) clearResumeState(storage, actorId, targetUserId);
      return { status: 'conflict', canonical: classified.canonical, error };
    }
    if (classified.type === 'idempotency') {
      return { status: 'idempotency', error };
    }
    if (classified.type === 'attention') {
      return { status: 'attention', error, classified };
    }
    return { status: 'error', error, classified };
  }
}

function mapCanonicalFromOperation(result) {
  return {
    targetUserId: result.operation.targetUserId,
    profilePictureImageId: result.operation.profilePictureImageId,
    profilePictureUrl: result.operation.profilePictureUrl,
  };
}
